<template>
    <Head title="Asset Categories" />

    <div class="py-6">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <!-- Header -->
            <div class="flex justify-between items-center mb-6">
                <div class="flex items-center gap-3">
                    <div>
                        <h1 class="text-2xl font-semibold text-gray-900 dark:text-gray-100">Asset Categories</h1>
                        <p class="mt-1 text-sm text-gray-600 dark:text-gray-400 dark:text-secondary-500">Organize and manage your asset categories</p>
                    </div>
                    <InfoButton title="Asset Categories Management" variant="default">
                        <p>
                            <strong class="text-secondary-900 dark:text-white">About this page</strong><br>
                            Asset Categories help you organize and classify assets in a hierarchical structure. Create parent
                            and child categories to group similar assets, making it easier to filter, report, and manage your
                            equipment. Categories support nested hierarchies (e.g., "Equipment → HVAC → Air Handlers"), allowing
                            for flexible organization that matches your facility structure.
                        </p>
                        <p>
                            <strong class="text-secondary-900 dark:text-white">How to use</strong><br>
                            • <strong>View Modes:</strong> Switch between Hierarchy view (tree structure showing parent-child
                              relationships) and List view (flat table with all categories)<br>
                            • <strong>Create Categories:</strong> Click "New Category" to add top-level or child categories.
                              Use parent categories for broad groupings, child categories for specific types<br>
                            • <strong>Search:</strong> Type category names to quickly find specific classifications<br>
                            • <strong>Filter by Status:</strong> Show all, active only, or inactive categories<br>
                            • <strong>Quick Stats:</strong> View total categories, active count, root categories (top-level),
                              and categories that have assets assigned<br>
                            • <strong>Hierarchy View:</strong> See the complete category tree with expandable branches. This
                              view shows how categories relate to each other<br>
                            • <strong>List View:</strong> Paginated table showing all categories with parent relationships,
                              asset counts, and status
                        </p>
                        <p>
                            <strong class="text-secondary-900 dark:text-white">Tips</strong><br>
                            • Use <strong>hierarchical structure</strong> to match your facility organization (e.g., Building
                              Systems → Electrical → Lighting)<br>
                            • <strong>Keep categories active</strong> - Deactivating a category doesn't delete it but hides
                              it from asset selection menus<br>
                            • <strong>Categories with assets</strong> stat shows which categories are actually being used<br>
                            • <strong>Root categories</strong> are top-level classifications; add child categories beneath
                              them for granularity<br>
                            • <strong>Asset counts</strong> in list view show how many assets are directly assigned to each
                              category<br>
                            • Plan category structure before creating many assets - restructuring later requires updating
                              asset records<br>
                            • Use descriptive names that technicians and managers will understand (avoid abbreviations)
                        </p>
                    </InfoButton>
                </div>
                <div class="flex items-center space-x-3">
                    <Link
                        :href="route('settings.maintenance.assets.index')"
                        class="inline-flex items-center px-4 py-2 bg-gray-600 dark:bg-gray-700 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition"
                    >
                        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                        Back to Assets
                    </Link>
                    <button
                        @click="openCreateModal"
                        class="inline-flex items-center px-4 py-2 bg-blue-600 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition"
                    >
                        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                        </svg>
                        New Category
                    </button>
                </div>
            </div>

            <!-- Statistics Cards -->
            <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <!-- Total Categories -->
                <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                    <div class="flex items-center">
                        <div class="flex-shrink-0 bg-blue-100 dark:bg-blue-900/30 rounded-md p-3">
                            <svg class="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                            </svg>
                        </div>
                        <div class="ml-4">
                            <p class="text-sm font-medium text-gray-600 dark:text-gray-400 dark:text-secondary-500">Total Categories</p>
                            <p class="text-2xl font-semibold text-blue-600 dark:text-blue-400">{{ stats.total_categories }}</p>
                        </div>
                    </div>
                </div>

                <!-- Active Categories -->
                <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                    <div class="flex items-center">
                        <div class="flex-shrink-0 bg-green-100 dark:bg-green-900/30 rounded-md p-3">
                            <svg class="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <div class="ml-4">
                            <p class="text-sm font-medium text-gray-600 dark:text-gray-400 dark:text-secondary-500">Active Categories</p>
                            <p class="text-2xl font-semibold text-green-600 dark:text-green-400">{{ stats.active_categories }}</p>
                        </div>
                    </div>
                </div>

                <!-- Root Categories -->
                <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                    <div class="flex items-center">
                        <div class="flex-shrink-0 bg-purple-100 dark:bg-purple-900/30 rounded-md p-3">
                            <svg class="w-6 h-6 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                            </svg>
                        </div>
                        <div class="ml-4">
                            <p class="text-sm font-medium text-gray-600 dark:text-gray-400 dark:text-secondary-500">Root Categories</p>
                            <p class="text-2xl font-semibold text-purple-600 dark:text-purple-400">{{ stats.root_categories }}</p>
                        </div>
                    </div>
                </div>

                <!-- Categories with Assets -->
                <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                    <div class="flex items-center">
                        <div class="flex-shrink-0 bg-orange-100 dark:bg-orange-900/30 rounded-md p-3">
                            <svg class="w-6 h-6 text-orange-600 dark:text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                            </svg>
                        </div>
                        <div class="ml-4">
                            <p class="text-sm font-medium text-gray-600 dark:text-gray-400 dark:text-secondary-500">With Assets</p>
                            <p class="text-2xl font-semibold text-orange-600 dark:text-orange-400">{{ stats.categories_with_assets }}</p>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Filters and View Toggle -->
            <div class="bg-white dark:bg-gray-800 rounded-lg shadow mb-6">
                <div class="p-4 border-b border-gray-200 dark:border-gray-700">
                    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <!-- Search -->
                        <div class="flex-1 max-w-lg">
                            <div class="relative">
                                <input
                                    v-model="searchQuery"
                                    type="text"
                                    placeholder="Search categories..."
                                    class="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100"
                                    @input="debounceSearch"
                                />
                                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                    </svg>
                                </div>
                            </div>
                        </div>

                        <!-- View Toggle -->
                        <div class="flex items-center space-x-2">
                            <button
                                @click="changeView('hierarchy')"
                                :class="[
                                    'px-3 py-2 rounded-md text-sm font-medium',
                                    viewMode === 'hierarchy'
                                        ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                                        : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700'
                                ]"
                            >
                                <svg class="w-5 h-5 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                                </svg>
                                Hierarchy
                            </button>
                            <button
                                @click="changeView('list')"
                                :class="[
                                    'px-3 py-2 rounded-md text-sm font-medium',
                                    viewMode === 'list'
                                        ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                                        : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700'
                                ]"
                            >
                                <svg class="w-5 h-5 inline mr-1" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M4 6h16M4 12h16M4 18h16" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                                </svg>
                                List
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Filter Pills -->
                <div class="p-4 flex flex-wrap gap-2">
                    <select
                        v-model="selectedFilters.is_active"
                        class="rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 text-sm focus:ring-blue-500 focus:border-blue-500"
                        @change="applyFilters"
                    >
                        <option value="">All Status</option>
                        <option value="1">Active</option>
                        <option value="0">Inactive</option>
                    </select>

                    <button
                        v-if="hasActiveFilters"
                        @click="clearFilters"
                        class="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 border border-gray-300 dark:border-gray-600 rounded-md"
                    >
                        Clear Filters
                    </button>
                </div>
            </div>

            <!-- Hierarchy View -->
            <div v-if="viewMode === 'hierarchy'" class="space-y-4">
                <template v-if="isHierarchyData && categories.length > 0">
                    <CategoryTree
                        v-for="category in categories.filter(cat => cat !== null)"
                        :key="category.id"
                        :category="category"
                        :level="0"
                    />
                </template>

                <!-- Empty State -->
                <div v-else-if="isHierarchyData && categories.length === 0" class="bg-white dark:bg-gray-800 rounded-lg shadow p-12 text-center">
                    <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                    </svg>
                    <h3 class="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">No categories found</h3>
                    <p class="mt-1 text-sm text-gray-500 dark:text-gray-400 dark:text-secondary-500">Get started by creating a new asset category.</p>
                </div>
            </div>

            <!-- List View -->
            <div v-else class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden mb-6">
                <table v-if="isListData && categories.data && categories.data.length > 0" class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead class="bg-gray-50 dark:bg-gray-700">
                        <tr>
                            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                Category
                            </th>
                            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                Parent
                            </th>
                            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                Assets
                            </th>
                            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                Status
                            </th>
                            <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        <tr v-for="category in categories.data" :key="category.id" class="hover:bg-gray-50 dark:hover:bg-gray-700 dark:hover:bg-gray-600">
                            <td class="px-6 py-4">
                                <div>
                                    <div class="text-sm font-medium text-gray-900 dark:text-gray-100">{{ category.name }}</div>
                                    <div v-if="category.description" class="text-sm text-gray-500 dark:text-gray-400 dark:text-secondary-500">{{ category.description }}</div>
                                </div>
                            </td>
                            <td class="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                                <Link v-if="category.parent" :href="route('settings.maintenance.asset-categories.show', category.parent.id)" class="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300">
                                    {{ category.parent.name }}
                                </Link>
                                <span v-else class="text-gray-500 dark:text-gray-400 dark:text-secondary-500">-</span>
                            </td>
                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                {{ category.assets_count || 0 }}
                            </td>
                            <td class="px-6 py-4 whitespace-nowrap">
                                <span
                                    :class="[
                                        'px-2 inline-flex text-xs leading-5 font-semibold rounded-full',
                                        category.is_active
                                            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                                            : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400'
                                    ]"
                                >
                                    {{ category.is_active ? 'Active' : 'Inactive' }}
                                </span>
                            </td>
                            <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                <Link
                                    :href="route('settings.maintenance.asset-categories.show', category.id)"
                                    class="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 dark:text-blue-400 dark:hover:text-blue-300"
                                >
                                    View
                                </Link>
                            </td>
                        </tr>
                    </tbody>
                </table>

                <!-- Empty State -->
                <div v-else-if="isListData && (!categories.data || categories.data.length === 0)" class="p-12 text-center">
                    <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                    </svg>
                    <h3 class="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">No categories found</h3>
                    <p class="mt-1 text-sm text-gray-500 dark:text-gray-400 dark:text-secondary-500">Get started by creating a new asset category.</p>
                </div>

                <!-- Pagination -->
                <div v-if="isListData && categories.data && categories.data.length > 0" class="bg-white dark:bg-gray-800 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 sm:px-6">
                    <div class="flex-1 flex justify-between sm:hidden">
                        <button
                            v-if="categories.prev_page_url"
                            @click="goToPage(categories.current_page - 1)"
                            class="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                        >
                            Previous
                        </button>
                        <button
                            v-if="categories.next_page_url"
                            @click="goToPage(categories.current_page + 1)"
                            class="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                        >
                            Next
                        </button>
                    </div>
                    <div class="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                        <div>
                            <p class="text-sm text-gray-700 dark:text-gray-300">
                                Showing
                                <span class="font-medium">{{ categories.from }}</span>
                                to
                                <span class="font-medium">{{ categories.to }}</span>
                                of
                                <span class="font-medium">{{ categories.total }}</span>
                                results
                            </p>
                        </div>
                        <div>
                            <nav class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                                <button
                                    v-for="(link, index) in categories.links"
                                    :key="index"
                                    @click="goToPageFromLink(link)"
                                    :disabled="!link.url || link.active"
                                    :class="[
                                        'relative inline-flex items-center px-4 py-2 border text-sm font-medium',
                                        link.active
                                            ? 'z-10 bg-blue-50 border-blue-500 text-blue-600 dark:bg-blue-900/30 dark:border-blue-400 dark:text-blue-400'
                                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600',
                                        !link.url || link.active ? 'cursor-not-allowed opacity-50' : ''
                                    ]"
                                    v-html="link.label"
                                />
                            </nav>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Create/Edit Modal -->
    <Teleport to="body">
        <Transition
            enter-active-class="transition ease-out duration-200"
            enter-from-class="opacity-0"
            enter-to-class="opacity-100"
            leave-active-class="transition ease-in duration-150"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
        >
            <div
                v-if="showModal"
                class="fixed inset-0 z-50 overflow-y-auto"
                aria-labelledby="modal-title"
                role="dialog"
                aria-modal="true"
            >
                <!-- Background backdrop -->
                <div
                    class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                    @click="closeModal"
                ></div>

                <!-- Modal panel -->
                <div class="flex min-h-full items-center justify-center p-4">
                    <div class="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
                        <div class="p-6">
                            <div class="flex items-center justify-between mb-4">
                                <h3 class="text-lg font-medium text-gray-900 dark:text-gray-100">
                                    {{ modalMode === 'create' ? 'Create Asset Category' : 'Edit Asset Category' }}
                                </h3>
                                <button
                                    @click="closeModal"
                                    class="text-gray-400 hover:text-gray-500"
                                >
                                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>

                            <form @submit.prevent="submitForm" class="space-y-4">
                                <!-- Name -->
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Name <span class="text-red-500">*</span>
                                    </label>
                                    <input
                                        v-model="form.name"
                                        type="text"
                                        required
                                        class="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                        placeholder="e.g., HVAC Equipment"
                                    />
                                </div>

                                <!-- Description -->
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Description
                                    </label>
                                    <textarea
                                        v-model="form.description"
                                        rows="3"
                                        class="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                        placeholder="Describe this category..."
                                    />
                                </div>

                                <!-- Parent Category -->
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Parent Category
                                    </label>
                                    <select
                                        v-model="form.parent_id"
                                        class="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                    >
                                        <option :value="null">None (Root Category)</option>
                                        <option v-for="category in allCategories" :key="category.id" :value="category.id">
                                            {{ category.path_name }}
                                        </option>
                                    </select>
                                </div>

                                <!-- Active Status -->
                                <div class="flex items-center">
                                    <input
                                        id="is_active"
                                        v-model="form.is_active"
                                        type="checkbox"
                                        class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded"
                                    />
                                    <label for="is_active" class="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                                        Active
                                    </label>
                                </div>

                                <!-- Actions -->
                                <div class="mt-6 flex justify-end space-x-3">
                                    <button
                                        type="button"
                                        @click="closeModal"
                                        class="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 dark:hover:bg-gray-600"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        :disabled="submitting"
                                        class="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <span v-if="submitting">{{ modalMode === 'create' ? 'Creating...' : 'Saving...' }}</span>
                                        <span v-else>{{ modalMode === 'create' ? 'Create' : 'Save' }}</span>
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<script setup>
import { ref, computed, Transition, Teleport } from 'vue';
import { Head, Link, router } from '@inertiajs/vue3';
import route from '@/Utils/route';
import CategoryTree from '@/Components/AssetCategories/CategoryTree.vue';
import InfoButton from '@/Components/Common/InfoButton.vue';

const props = defineProps({
    categories: [Object, Array],
    allCategories: Array,
    stats: Object,
    filters: Object,
});

const viewMode = ref(props.filters.view || 'hierarchy');
const searchQuery = ref(props.filters.search || '');
const selectedFilters = ref({
    is_active: props.filters.is_active || '',
});

const showModal = ref(false);
const modalMode = ref('create'); // 'create' or 'edit'
const submitting = ref(false);
const form = ref({
    name: '',
    description: '',
    parent_id: null,
    icon: '',
    color: '',
    is_active: true,
});

let searchTimeout = null;

// Computed properties
const isHierarchyData = computed(() => {
    return Array.isArray(props.categories);
});

const isListData = computed(() => {
    return props.categories && typeof props.categories === 'object' && 'data' in props.categories;
});

const hasActiveFilters = computed(() => {
    return selectedFilters.value.is_active || searchQuery.value;
});

// Methods
const debounceSearch = () => {
    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
        applyFilters();
    }, 500);
};

const changeView = (view) => {
    viewMode.value = view;
    const params = { view: view };
    if (searchQuery.value) params.search = searchQuery.value;
    if (selectedFilters.value.is_active !== '') params.is_active = selectedFilters.value.is_active;

    router.get(route('settings.maintenance.asset-categories.index'), params, {
        preserveScroll: true,
    });
};

const applyFilters = () => {
    const params = { view: viewMode.value };
    if (searchQuery.value) params.search = searchQuery.value;
    if (selectedFilters.value.is_active !== '') params.is_active = selectedFilters.value.is_active;

    router.get(route('settings.maintenance.asset-categories.index'), params, {
        preserveState: true,
        preserveScroll: true,
    });
};

const clearFilters = () => {
    searchQuery.value = '';
    selectedFilters.value = {
        is_active: '',
    };
    applyFilters();
};

const goToPage = (page) => {
    const params = { page, view: viewMode.value };
    if (searchQuery.value) params.search = searchQuery.value;
    if (selectedFilters.value.is_active !== '') params.is_active = selectedFilters.value.is_active;

    router.get(route('settings.maintenance.asset-categories.index'), params, {
        preserveState: true,
        preserveScroll: true,
    });
};

const goToPageFromLink = (link) => {
    if (!link.url || link.active) return;

    const url = new URL(link.url);
    const page = url.searchParams.get('page');

    if (page) {
        goToPage(page);
    }
};

const getCategoryColorClass = (category) => {
    if (category.color) {
        return `bg-${category.color}-100 text-${category.color}-600 dark:bg-${category.color}-900/30 dark:text-${category.color}-400`;
    }
    return 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400';
};

// Modal functions
const openCreateModal = () => {
    modalMode.value = 'create';
    form.value = {
        name: '',
        description: '',
        parent_id: null,
        icon: '',
        color: '',
        is_active: true,
    };
    showModal.value = true;
};

const closeModal = () => {
    showModal.value = false;
    form.value = {
        name: '',
        description: '',
        parent_id: null,
        icon: '',
        color: '',
        is_active: true,
    };
};

const submitForm = () => {
    submitting.value = true;

    const routeName = modalMode.value === 'create'
        ? 'settings.maintenance.asset-categories.store'
        : 'settings.maintenance.asset-categories.update';

    const method = modalMode.value === 'create' ? 'post' : 'put';

    router[method](
        route(routeName, modalMode.value === 'edit' ? form.value.id : undefined),
        form.value,
        {
            preserveScroll: true,
            onSuccess: () => {
                closeModal();
            },
            onFinish: () => {
                submitting.value = false;
            },
        }
    );
};
</script>
